import React from 'react';
import { formatHours } from '../../../utils/duration';

/**
 * Local, report-scoped turnaround visual — horizontal progress-bar rows with a
 * bottleneck badge on the slowest stage. Fed the SAME `rows` array the table
 * renders (OperationalSection's pipeline.turnaround), so chart and table are
 * provably the same data and there is NO extra fetch. Bottleneck logic ported
 * from the dashboard's TurnaroundByStageChart, but bound to report data.
 */
const TurnaroundChart = ({ rows }) => {
  const list = Array.isArray(rows) ? rows : [];
  const maxAvg = list.reduce((m, r) => Math.max(m, Number(r.avg_hours) || 0), 0);
  const bottleneckIdx = list.findIndex((r) => (Number(r.avg_hours) || 0) === maxAvg && maxAvg > 0);

  // chart-1 mixed toward muted, scaled 28%..100% by stage magnitude.
  const intensity = (h) => {
    const t = maxAvg > 0 ? h / maxAvg : 0;
    return `color-mix(in oklab, var(--dash-chart-1) ${Math.round(28 + t * 72)}%, var(--dash-muted))`;
  };

  if (list.length === 0) {
    return <div className="py-6 text-center text-[11px] text-dash-muted-foreground">No stage data available</div>;
  }

  return (
    <div className="space-y-1.5">
      {list.map((r, i) => {
        const h = Number(r.avg_hours) || 0;
        const width = Math.max(0, Number(r.pct_of_cycle) || 0);
        return (
          <div key={i} className="grid grid-cols-[16px_minmax(0,1fr)_64px_44px] items-center gap-2 text-[11px]">
            <span className="text-right tabular-nums text-dash-muted-foreground">{i + 1}</span>
            <div className="min-w-0">
              <div className="flex items-center gap-2">
                <span className="truncate text-dash-foreground">{r.stage}</span>
                {i === bottleneckIdx ? (
                  <span className="shrink-0 rounded-sm bg-dash-destructive/10 px-1.5 py-px text-[10px] font-medium uppercase tracking-wide text-dash-destructive">
                    Bottleneck
                  </span>
                ) : null}
              </div>
              <div className="mt-1 h-1.5 w-full rounded-full bg-dash-muted">
                <div className="h-full rounded-full" style={{ width: `${width}%`, backgroundColor: intensity(h) }} />
              </div>
            </div>
            {/* Not compact: this chart sits beside a table fed by the same array,
                and the two must read identically (smoke test 7.2). */}
            <span className="text-right tabular-nums font-medium text-dash-foreground">
              {formatHours(r.avg_hours)}
            </span>
            <span className="text-right tabular-nums text-dash-muted-foreground">
              {Math.round(Number(r.pct_of_cycle) || 0)}%
            </span>
          </div>
        );
      })}
    </div>
  );
};

export default TurnaroundChart;
